/**
 * AguaResumen.js — Panel de consumo de agua
 *
 *   1. KPIs del período (consumo total, promedio por filial, cobertura)
 *   2. Evolución mensual del consumo del condominio (área)
 *   3. Ranking de filiales por consumo (barras horizontales)
 *   4. Alertas de posible fuga (consumo muy por encima del propio promedio)
 *   5. Filiales sin lectura en el período
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import AguaShell from "../components/AguaShell";
import { getResumen, getSerie } from "../services/aguaService";
import { AZUL, AZUL_CLARO, AMBAR, ROJO, VERDE, aguaTheme, fechaCorta, hoyISO, m3 } from "../theme";

/** Primer día del mes, `n` meses atrás, en YYYY-MM-DD. */
function haceMeses(n) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

const PERIODOS = [3, 6, 12];

export default function AguaResumen() {
  const { t }     = useTranslation();
  const { theme } = useTheme();
  const isLight   = theme === "light";
  const tema      = aguaTheme(isLight);

  const [meses,    setMeses]    = useState(6);
  const [resumen,  setResumen]  = useState(null);
  const [serie,    setSerie]    = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error,    setError]    = useState("");

  useEffect(() => {
    setCargando(true); setError("");
    Promise.all([
      getResumen({ desde: haceMeses(meses - 1), hasta: hoyISO() }),
      getSerie({ meses }),
    ])
      .then(([r, s]) => { setResumen(r); setSerie(s); })
      .catch(e => setError(e.message))
      .finally(() => setCargando(false));
  }, [meses]);

  const datosSerie = useMemo(() => serie.map(p => {
    const [anio, mes] = p.mes.split("-");
    const nombres = t("agua.mesesCortos", { returnObjects: true });
    const etiqueta = Array.isArray(nombres) ? nombres[parseInt(mes, 10) - 1] : mes;
    return { mes: `${etiqueta} ${anio.slice(2)}`, consumo: parseFloat(p.consumo) };
  }), [serie, t]);

  const ranking = useMemo(() => (resumen?.por_filial || [])
    .filter(f => parseFloat(f.consumo_total) > 0)
    .slice(0, 10)
    .map(f => ({ filial: f.filial, consumo: parseFloat(f.consumo_total) })),
  [resumen]);

  const maxRanking = ranking.length ? ranking[0].consumo : 0;

  const tooltipStyle = {
    background: isLight ? "#fff" : "#1f2937",
    border: `1px solid ${isLight ? "#d9e0e8" : "#364250"}`,
    borderRadius: 10, fontSize: 13,
    color: isLight ? "#1e293b" : "#f1f5f9",
  };

  const KPIS = resumen ? [
    {
      icono: "fa-droplet", color: AZUL, label: t("agua.consumoTotal"),
      valor: m3(resumen.totales.consumo_total),
    },
    {
      icono: "fa-chart-simple", color: AZUL_CLARO, label: t("agua.promedioPorFilial"),
      valor: m3(resumen.totales.promedio_por_filial),
    },
    {
      icono: "fa-house-circle-check", color: VERDE, label: t("agua.cobertura"),
      valor: `${resumen.totales.filiales_con_lectura} / ${resumen.totales.filiales_totales}`,
    },
    {
      icono: "fa-triangle-exclamation",
      color: resumen.alertas.length ? ROJO : "var(--text-secondary)",
      label: t("agua.posiblesFugas"),
      valor: String(resumen.alertas.length),
    },
  ] : [];

  return (
    <AguaShell
      titulo={t("agua.tituloResumen")}
      descripcion={t("agua.descResumen")}
      icono="fa-chart-simple"
      acciones={
        <div style={{ display: "flex", gap: 6 }}>
          {PERIODOS.map(n => (
            <button key={n} onClick={() => setMeses(n)} className="agua-btn" style={{
              padding: "8px 13px", fontSize: 12.5,
              background: meses === n ? AZUL : "transparent",
              color: meses === n ? "#fff" : AZUL,
              border: `1px solid ${AZUL}`,
            }}>
              {t("agua.nMeses", { n })}
            </button>
          ))}
        </div>
      }
    >
      {error && (
        <div className="agua-card" style={{
          marginBottom: 14, color: "#ef4444",
          background: "rgba(239,68,68,0.09)", border: "1px solid rgba(239,68,68,0.3)",
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 8 }} />{error}
        </div>
      )}

      {cargando ? (
        <p style={{ textAlign: "center", color: "var(--text-secondary)", padding: 40 }}>
          {t("common.cargando")}
        </p>
      ) : !resumen ? null : (
        <>
          {/* ── KPIs ── */}
          <div className="agua-grid" style={{
            gridTemplateColumns: "repeat(auto-fit,minmax(165px,1fr))", marginBottom: 14,
          }}>
            {KPIS.map(({ icono, color, label, valor }) => (
              <div key={label} className="agua-card" style={{
                boxShadow: tema.cardShadow, display: "flex", alignItems: "center", gap: 12,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                  background: tema.chipBg, border: `1px solid ${tema.chipBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className={`fa-solid ${icono}`} style={{ color, fontSize: 16 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
                    textTransform: "uppercase", color: "var(--text-secondary)",
                  }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: "var(--text-primary)" }}>
                    {valor}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Evolución mensual ── */}
          <div className="agua-card" style={{ marginBottom: 14, boxShadow: tema.cardShadow }}>
            <h2 style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 4px" }}>
              {t("agua.evolucionMensual")}
            </h2>
            <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 16px" }}>
              {t("agua.evolucionDesc")}
            </p>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={datosSerie} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradAgua" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={AZUL_CLARO} stopOpacity={0.55} />
                      <stop offset="95%" stopColor={AZUL_CLARO} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tema.grid} vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                         axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                         axisLine={false} tickLine={false} width={54} />
                  <Tooltip contentStyle={tooltipStyle}
                           formatter={v => [m3(v), t("agua.consumo")]} />
                  <Area type="monotone" dataKey="consumo" stroke={AZUL} strokeWidth={2.5}
                        fill="url(#gradAgua)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Ranking por filial ── */}
          <div className="agua-card" style={{ marginBottom: 14, boxShadow: tema.cardShadow }}>
            <h2 style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 4px" }}>
              {t("agua.rankingTitulo")}
            </h2>
            <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 16px" }}>
              {t("agua.rankingDesc")}
            </p>
            {ranking.length === 0 ? (
              <p style={{ color: "var(--text-secondary)", fontSize: 13, margin: 0 }}>
                {t("agua.sinDatosPeriodo")}
              </p>
            ) : (
              <div style={{ width: "100%", height: Math.max(180, ranking.length * 38) }}>
                <ResponsiveContainer>
                  <BarChart data={ranking} layout="vertical"
                            margin={{ top: 0, right: 16, left: 6, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tema.grid} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                           axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="filial" width={72}
                           tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
                           axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: tema.chipBg }}
                             formatter={v => [m3(v), t("agua.consumo")]} />
                    <Bar dataKey="consumo" radius={[0, 7, 7, 0]} barSize={20}>
                      {ranking.map((r, i) => (
                        // El más consumidor se pinta más fuerte para que salte a la vista
                        <Cell key={r.filial}
                              fill={i === 0 ? AZUL : AZUL_CLARO}
                              fillOpacity={maxRanking ? 0.45 + 0.55 * (r.consumo / maxRanking) : 1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── Alertas de posible fuga ── */}
          {resumen.alertas.length > 0 && (
            <div className="agua-card" style={{
              marginBottom: 14, boxShadow: tema.cardShadow,
              borderLeft: `4px solid ${ROJO}`,
            }}>
              <h2 style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 4px" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ color: ROJO, marginRight: 8 }} />
                {t("agua.alertasTitulo")}
              </h2>
              <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 14px" }}>
                {t("agua.alertasDesc")}
              </p>
              <div className="agua-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))" }}>
                {resumen.alertas.map(a => (
                  <Link key={a.filial_id} to={`/agua/historial?filial=${a.filial_id}`} style={{
                    textDecoration: "none", padding: "13px 15px", borderRadius: 11,
                    background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.28)",
                    display: "block",
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>
                      {a.filial}
                    </div>
                    <div style={{ fontSize: 12.5, color: ROJO, fontWeight: 700, marginTop: 3 }}>
                      {t("agua.vecesElPromedio", { veces: a.veces })}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
                      {m3(a.consumo)} · {t("agua.promedioHistorico")} {m3(a.promedio)}
                    </div>
                    {a.muestras < 3 && (
                      <div style={{ fontSize: 11, color: AMBAR, marginTop: 5 }}>
                        <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }} />
                        {t("agua.pocasMuestras", { n: a.muestras })}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ── Filiales sin lectura ── */}
          {resumen.sin_lectura.length > 0 && (
            <div className="agua-card" style={{ boxShadow: tema.cardShadow }}>
              <h2 style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 4px" }}>
                {t("agua.sinLecturaTitulo", { n: resumen.sin_lectura.length })}
              </h2>
              <p style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 14px" }}>
                {t("agua.sinLecturaDesc")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {resumen.sin_lectura.map(f => (
                  <span key={f.filial_id} title={
                    f.ultima_fecha
                      ? `${t("agua.ultima")}: ${fechaCorta(f.ultima_fecha)}`
                      : t("agua.sinLecturasPrevias")
                  } style={{
                    padding: "7px 12px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                    background: "rgba(245,158,11,0.09)", border: "1px solid rgba(245,158,11,0.3)",
                    color: AMBAR,
                  }}>
                    {f.filial}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </AguaShell>
  );
}
